import { useCallback, useMemo } from 'react';

import { VSpacingContainer, Cell, CellText, Card } from '@hh.ru/magritte-ui';
import { ArrowLeftOutlinedSize24 } from '@hh.ru/magritte-ui/icon';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import { useUpdateDraftFilter } from 'src/components/Search/Common/Filters/hooks/draft/useUpdateDraftFilter';
import { toggleValueInArray } from 'src/components/Search/Common/Filters/utils/toggleValueInArray';
import MetroIcon from 'src/components/Search/Common/Metro/MetroIcon';
import { ModelDataMetro } from 'src/components/Search/Common/Metro/types';
import { handleMetroOptionChange } from 'src/components/Search/Resume/Filters/Geo/Filters/Metro/utils/metroUtils';
import SelectorOption from 'src/components/Search/Resume/Filters/Geo/SelectorOption';
import translation from 'src/components/translation';
import { useSelector } from 'src/hooks/useSelector';
import { EMPTY_CLUSTER } from 'src/models/search/common/clusters';
import { ClusterKey } from 'src/types/search/common/clusters';

interface LineContentProps {
    line: ModelDataMetro;
    isSingleLine?: boolean;
    onReturn: () => void;
}

const TrlKeys = {
    metroReturnButton: 'search.resume.filters.geo.metro.return',
};

/**
 * Содержимое открытой линии метро.
 */
const LineContent: TranslatedComponent<LineContentProps> = ({ line, isSingleLine, onReturn, trls }) => {
    const { groups, selectedValues } = useSelector(
        (state) => state.searchDraftClusters[ClusterKey.Metro] || EMPTY_CLUSTER
    );
    const applyDraftFilter = useUpdateDraftFilter(ClusterKey.Metro);

    const { id: lineId, text: lineText, items, additional } = line;
    const isLineChecked = useMemo(() => selectedValues.includes(lineId), [selectedValues, lineId]);
    const lineResumeCounter = useMemo(() => {
        let sum = 0;

        items?.forEach((station) => {
            sum += groups[station.id]?.count || 0;
        });

        return sum;
    }, [items, groups]);

    const handleLineCheck = useCallback(() => {
        applyDraftFilter(toggleValueInArray(lineId, selectedValues));
    }, [lineId, selectedValues, applyDraftFilter]);

    return (
        <VSpacingContainer default={12}>
            {!isSingleLine && (
                <Card onClick={onReturn} padding={16} paddingLeft={0} borderRadius={12} stretched>
                    <Cell left={<ArrowLeftOutlinedSize24 initialColor="secondary" />}>
                        <CellText>{trls[TrlKeys.metroReturnButton]}</CellText>
                    </Cell>
                </Card>
            )}
            <SelectorOption
                isSelected={isLineChecked}
                isIndeterminate={isLineChecked}
                onClick={handleLineCheck}
                text={lineText}
                textIcon={<MetroIcon isLine color={additional?.color || ''} />}
                count={lineResumeCounter}
            />
            {items?.map(({ id, text }) => {
                const isChecked = selectedValues.some((item) => item === id || item === lineId);

                return (
                    <SelectorOption
                        key={id}
                        isSelected={isChecked}
                        onClick={() => {
                            handleMetroOptionChange(id, selectedValues, items, applyDraftFilter);
                        }}
                        text={text}
                        textIcon={<MetroIcon isLine={false} color={additional?.color || ''} />}
                        count={groups[id]?.count || 0}
                    />
                );
            })}
        </VSpacingContainer>
    );
};

export default translation(LineContent);
